import React, { useEffect, useState } from "react";

import { AddProject } from "./components/AddProject";
import { ProjectSheet } from "./components/ProjectSheet";
import SpecialRateModal from "./components/SpecialRateModal";
import { UpdateProject } from "./components/UpdateProject";
import {
  ProjectContextProvider,
  useProjectContext,
} from "./context/projectCtx";
import { useProjectSession } from "./hooks/useProjectSession";
import { ProjectsTable } from "./table/ProjectsTable";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import { sortingAndSearchFilterParams } from "@/constants/constants";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { SpecialRateFor } from "@/constants/special-rate.constants";
import { projectTab } from "@/constants/tabs.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import { useApiInvalidator } from "@/hooks/useApiInvalidator";
import { useGetParams } from "@/hooks/useGetParams";
import useLocalPaginationHelper from "@/hooks/useLocalPaginationHelper";
import { usePagination } from "@/hooks/usePagination";
import { useSocket } from "@/hooks/useSocket";
import { SocketData } from "@/interfaces/globals";
import { ProjectStatus } from "@/interfaces/project.interface";
import {
  projectApi,
  useGetProjectsByStatusQuery,
} from "@/redux/api/projectsQuery";
import { tableUpdateToast } from "@/utils/helpers";

const Projects = (): React.JSX.Element => {
  const {
    isAddModalOpen,
    setIsModalOpen,
    isShowSpecialPriceModal,
    setIsShowSpecialPriceModal,
    isUpdateModalOpen,
    setIsUpdateModalOpen,
  } = useProjectContext();

  const [isSheet, setIsSheet] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(projectTab[0].value);
  const { setLocalPagination, paginationQuery } = useLocalPaginationHelper();
  const { clearProjectData } = useProjectSession(); // Add this

  const filter = useGetParams(["customerId", ...sortingAndSearchFilterParams]);

  // Queries
  const { data: res, ...rest } = useGetProjectsByStatusQuery({
    status: activeTab,
    filter: "&" + filter?.substring(1) + paginationQuery,
  });
  const pagination = usePagination(res?.pagination, 10, setLocalPagination);

  // socket actions
  const { socket } = useSocket("/");
  const { invalidateTags } = useApiInvalidator<typeof projectApi>();

  useEffect(() => {
    socket?.on(SOCKET_KEYS.PROJECTS, (data: SocketData) => {
      invalidateTags(projectApi, ["projects"]);
      tableUpdateToast(data, PROJECT_CONSTANTS.MESSAGES.TABLE_UPDATE);
    });

    return (): void => {
      socket?.off(SOCKET_KEYS.PROJECTS);
    };
  }, [socket, rest, invalidateTags]);

  const handleSheetToggle = (): void => {
    if (isSheet) {
      // Clear session data when closing the sheet
      clearProjectData();
    }
    setIsSheet(!isSheet);
  };

  return (
    <DataTableSlotContextProvider>
      <CustomHeader
        title={"Project"}
        activeTab={activeTab}
        tabs={projectTab}
        setActiveTab={setActiveTab}
      />

      <FetchWrapper {...rest} isTable>
        <ProjectsTable
          setIsUpdateModalOpen={setIsUpdateModalOpen}
          data={res?.data ?? []}
          toggleModal={() => setIsModalOpen(!isAddModalOpen)}
          sheetToggle={handleSheetToggle}
          isOngoing={activeTab === ProjectStatus.ONGOING.toString()}
          pagination={pagination}
          toggleSpecialRateModal={() =>
            setIsShowSpecialPriceModal(!isShowSpecialPriceModal)
          }
        />
      </FetchWrapper>

      {isSheet && (
        <ProjectSheet isOpen={isSheet} toggleOpen={handleSheetToggle} />
      )}

      {isAddModalOpen && (
        <AddProject
          isOpen={isAddModalOpen}
          toggleModal={() => setIsModalOpen(!isAddModalOpen)}
        />
      )}
      {isUpdateModalOpen && (
        <UpdateProject
          isUpdateModalOpen={isUpdateModalOpen?.isOpen}
          projectId={isUpdateModalOpen?.id}
          toggleModal={() =>
            setIsUpdateModalOpen({
              isOpen: !isUpdateModalOpen?.isOpen,
              id: "",
            })
          }
        />
      )}
      {isShowSpecialPriceModal && (
        <SpecialRateModal
          isOpen={isShowSpecialPriceModal}
          toggleModal={() =>
            setIsShowSpecialPriceModal(!isShowSpecialPriceModal)
          }
          forType={SpecialRateFor.PROJECT}
        />
      )}
    </DataTableSlotContextProvider>
  );
};

const ProjectWrapper = (): React.JSX.Element => {
  return (
    <ProjectContextProvider>
      <Projects />
    </ProjectContextProvider>
  );
};

export default ProjectWrapper;
